import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveStavba } from '../../features/stavba-context/StavbaContext';
import { cx } from '../../lib/cx';
import styles from './StavbaSwitcher.module.css';

/**
 * Přepínač aktivní stavby v sidebaru. Místo holého <select> nabízí:
 *  - výběr ze seznamu staveb (s vyznačením té aktivní),
 *  - volbu „Žádná stavba" — práce mimo stavbu (účetní, zkoušení; scoped
 *    moduly pak zobrazí StavbaGate místo toho, aby se omylem psalo do ostré
 *    stavby),
 *  - odkaz na správu staveb (/app/stavby).
 */
export function StavbaSwitcher({ variant = 'sidebar' }: { variant?: 'sidebar' | 'drawer' } = {}) {
  const { activeStavba, stavby, setActiveStavbaId } = useActiveStavba();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (stavby.length === 0) return null;

  function pick(id: number | null) {
    setActiveStavbaId(id);
    setOpen(false);
  }

  return (
    <div className={cx(styles.wrap, variant === 'drawer' && styles.wrapDrawer)} ref={wrapRef}>
      <div className={styles.label}>Aktivní stavba</div>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={cx(styles.dot, !activeStavba && styles.dotOff)} aria-hidden />
        <span className={styles.meta}>
          <span className={styles.name}>{activeStavba ? activeStavba.nazev : 'Žádná stavba'}</span>
          <span className={styles.sub}>
            {activeStavba ? activeStavba.zakazka_cislo || '—' : 'mimo stavbu'}
          </span>
        </span>
        <span className={cx(styles.caret, open && styles.caretOpen)} aria-hidden>
          ⌄
        </span>
      </button>

      {open && (
        <div className={styles.menu} role="listbox" aria-label="Vyberte stavbu">
          <div className={styles.menuScroll}>
            {stavby.map((s) => {
              const isActive = activeStavba?.id === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={cx(styles.option, isActive && styles.optionActive)}
                  onClick={() => pick(s.id)}
                >
                  <span className={styles.check} aria-hidden>
                    {isActive ? '✓' : ''}
                  </span>
                  <span className={styles.optMeta}>
                    <span className={styles.optName}>{s.nazev || '— bez názvu —'}</span>
                    {s.zakazka_cislo && (
                      <span className={styles.optSub}>zakázka {s.zakazka_cislo}</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <div className={styles.divider} />

          <button
            type="button"
            role="option"
            aria-selected={!activeStavba}
            className={cx(styles.option, !activeStavba && styles.optionActive)}
            onClick={() => pick(null)}
          >
            <span className={styles.check} aria-hidden>
              {!activeStavba ? '✓' : ''}
            </span>
            <span className={styles.optMeta}>
              <span className={styles.optName}>Žádná stavba</span>
              <span className={styles.optSub}>pracovat mimo stavbu</span>
            </span>
          </button>

          <button
            type="button"
            className={styles.manage}
            onClick={() => {
              setOpen(false);
              navigate('/app/stavby');
            }}
          >
            Spravovat stavby →
          </button>
        </div>
      )}
    </div>
  );
}
